import { useState, useEffect, useCallback } from 'react';
import { 
  Image, Package, BarChart3, Plus, Pencil, Trash2, 
  GripVertical, Eye, EyeOff, Save, X, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { siteContentApi } from '@/lib/api';

// Example Site Card
const ExampleCard = ({ example, onEdit, onDelete, onToggleActive }) => (
  <Card className="group relative overflow-hidden">
    <div className="aspect-[4/3] overflow-hidden bg-slate-100">
      <img 
        src={example.image_url} 
        alt={example.name}
        className="w-full h-full object-cover"
        onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=No+Image'; }}
      />
      {!example.active && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <span className="text-white font-semibold bg-black/50 px-3 py-1 rounded">Hidden</span>
        </div>
      )}
    </div>
    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 truncate">{example.name}</h3>
          <p className="text-sm text-slate-500">{example.category}</p>
          {example.website_url && example.website_url !== '#' && (
            <a 
              href={example.website_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-orange-500 hover:underline truncate block"
            >
              {example.website_url}
            </a>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={() => onToggleActive(example)}
            data-testid={`toggle-example-${example.id}`}
          >
            {example.active ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={() => onEdit(example)}
            data-testid={`edit-example-${example.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-red-500 hover:text-red-700"
            onClick={() => onDelete(example)}
            data-testid={`delete-example-${example.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Pricing Package Card
const PackageCard = ({ pkg, onEdit, onDelete, onToggleActive, onTogglePopular }) => (
  <Card className={`relative ${pkg.is_popular ? 'ring-2 ring-orange-500' : ''}`}>
    {pkg.is_popular && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
        Popular
      </div>
    )}
    <CardHeader className="pb-2">
      <div className="flex items-start justify-between">
        <div>
          <CardTitle className="text-xl">{pkg.name}</CardTitle>
          <CardDescription>{pkg.description}</CardDescription>
        </div>
        {!pkg.active && (
          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">Hidden</span>
        )}
      </div>
    </CardHeader>
    <CardContent>
      <div className="mb-4">
        <span className="text-3xl font-bold">£{pkg.price_monthly}</span>
        <span className="text-slate-500">/month</span>
        <p className="text-sm text-slate-400">+ £{pkg.setup_fee} setup</p>
      </div>
      <ul className="space-y-2 mb-4 text-sm">
        {pkg.features?.slice(0, 4).map((feature, idx) => (
          <li key={idx} className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500" />
            <span>{feature}</span>
          </li>
        ))}
        {pkg.features?.length > 4 && (
          <li className="text-slate-400">+ {pkg.features.length - 4} more</li>
        )}
      </ul>
      <div className="flex items-center gap-2 pt-2 border-t">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onToggleActive(pkg)}
          data-testid={`toggle-pkg-${pkg.id}`}
        >
          {pkg.active ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
          {pkg.active ? 'Visible' : 'Hidden'}
        </Button>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onTogglePopular(pkg)}
          data-testid={`popular-pkg-${pkg.id}`}
        >
          {pkg.is_popular ? '★ Popular' : '☆ Set Popular'}
        </Button>
        <div className="flex-1" />
        <Button variant="ghost" size="icon" onClick={() => onEdit(pkg)} data-testid={`edit-pkg-${pkg.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => onDelete(pkg)} data-testid={`delete-pkg-${pkg.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Stat Card
const StatCard = ({ stat, onEdit, onDelete, onToggleActive }) => (
  <Card className={`${!stat.active ? 'opacity-50' : ''}`}>
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
        <div className="text-sm text-slate-500">{stat.label}</div>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" onClick={() => onToggleActive(stat)} data-testid={`toggle-stat-${stat.id}`}>
          {stat.active ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onEdit(stat)} data-testid={`edit-stat-${stat.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => onDelete(stat)} data-testid={`delete-stat-${stat.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Example Form Modal
const ExampleFormModal = ({ open, onClose, example, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    category: '',
    image_url: '',
    website_url: '',
    order: 0,
    active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (example) {
      setForm({
        name: example.name || '',
        category: example.category || '',
        image_url: example.image_url || '',
        website_url: example.website_url || '',
        order: example.order || 0,
        active: example.active !== false
      });
    } else {
      setForm({ name: '', category: '', image_url: '', website_url: '', order: 0, active: true });
    }
  }, [example, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.image_url) {
      toast.error('Name and image URL are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{example ? 'Edit Example Site' : 'Add Example Site'}</DialogTitle>
          <DialogDescription>
            Showcase performer websites on your marketing page
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Performer Name *</Label>
            <Input 
              id="name" 
              value={form.name} 
              onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder="e.g. Tim Howar"
              data-testid="example-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Input 
              id="category" 
              value={form.category} 
              onChange={(e) => setForm({...form, category: e.target.value})}
              placeholder="e.g. Singer / West End"
              data-testid="example-category-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="image_url">Image URL *</Label>
            <Input 
              id="image_url" 
              value={form.image_url} 
              onChange={(e) => setForm({...form, image_url: e.target.value})}
              placeholder="https://..."
              data-testid="example-image-input"
            />
            {form.image_url && (
              <img 
                src={form.image_url} 
                alt="Preview" 
                className="w-full h-32 object-cover rounded-lg mt-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="website_url">Website URL</Label>
            <Input 
              id="website_url" 
              value={form.website_url} 
              onChange={(e) => setForm({...form, website_url: e.target.value})}
              placeholder="https://..."
              data-testid="example-website-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch 
              id="active"
              checked={form.active}
              onCheckedChange={(checked) => setForm({...form, active: checked})}
            />
            <Label htmlFor="active">Show on website</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-example-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Package Form Modal
const PackageFormModal = ({ open, onClose, pkg, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    price_monthly: 0,
    setup_fee: 0,
    description: '',
    features: [],
    is_popular: false,
    order: 0,
    active: true
  });
  const [featuresText, setFeaturesText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (pkg) {
      setForm({
        name: pkg.name || '',
        price_monthly: pkg.price_monthly || 0,
        setup_fee: pkg.setup_fee || 0,
        description: pkg.description || '',
        features: pkg.features || [],
        is_popular: pkg.is_popular || false,
        order: pkg.order || 0,
        active: pkg.active !== false
      });
      setFeaturesText((pkg.features || []).join('\n'));
    } else {
      setForm({ name: '', price_monthly: 0, setup_fee: 0, description: '', features: [], is_popular: false, order: 0, active: true });
      setFeaturesText('');
    }
  }, [pkg, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error('Package name is required');
      return;
    }
    setSaving(true);
    try {
      const features = featuresText.split('\n').map(f => f.trim()).filter(f => f);
      await onSave({ ...form, features });
      onClose();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{pkg ? 'Edit Package' : 'Add Package'}</DialogTitle>
          <DialogDescription>
            Configure pricing packages for your marketing page
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Package Name *</Label>
              <Input 
                id="name" 
                value={form.name} 
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="e.g. Pro"
                data-testid="pkg-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input 
                id="description" 
                value={form.description} 
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="e.g. For serious performers"
                data-testid="pkg-desc-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price">Monthly Price (£)</Label>
              <Input 
                id="price" 
                type="number"
                value={form.price_monthly} 
                onChange={(e) => setForm({...form, price_monthly: parseInt(e.target.value) || 0})}
                data-testid="pkg-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="setup">Setup Fee (£)</Label>
              <Input 
                id="setup" 
                type="number"
                value={form.setup_fee} 
                onChange={(e) => setForm({...form, setup_fee: parseInt(e.target.value) || 0})}
                data-testid="pkg-setup-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="features">Features (one per line)</Label>
            <Textarea 
              id="features" 
              value={featuresText} 
              onChange={(e) => setFeaturesText(e.target.value)}
              placeholder="Multi-page website&#10;Video gallery&#10;Custom domain"
              rows={5}
              data-testid="pkg-features-input"
            />
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch 
                id="active"
                checked={form.active}
                onCheckedChange={(checked) => setForm({...form, active: checked})}
              />
              <Label htmlFor="active">Show on website</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                id="popular"
                checked={form.is_popular}
                onCheckedChange={(checked) => setForm({...form, is_popular: checked})}
              />
              <Label htmlFor="popular">Mark as popular</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-pkg-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Stat Form Modal
const StatFormModal = ({ open, onClose, stat, onSave }) => {
  const [form, setForm] = useState({ value: '', label: '', order: 0, active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (stat) {
      setForm({
        value: stat.value || '',
        label: stat.label || '',
        order: stat.order || 0,
        active: stat.active !== false
      });
    } else {
      setForm({ value: '', label: '', order: 0, active: true });
    }
  }, [stat, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.value || !form.label) {
      toast.error('Value and label are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{stat ? 'Edit Stat' : 'Add Stat'}</DialogTitle>
          <DialogDescription>
            Stats shown in the hero section
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="value">Value *</Label>
            <Input 
              id="value" 
              value={form.value} 
              onChange={(e) => setForm({...form, value: e.target.value})}
              placeholder="e.g. 500+ or 98%"
              data-testid="stat-value-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="label">Label *</Label>
            <Input 
              id="label" 
              value={form.label} 
              onChange={(e) => setForm({...form, label: e.target.value})}
              placeholder="e.g. Performers Online"
              data-testid="stat-label-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch 
              id="active"
              checked={form.active}
              onCheckedChange={(checked) => setForm({...form, active: checked})}
            />
            <Label htmlFor="active">Show on website</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-stat-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ open, onClose, onConfirm, itemName }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle>Delete {itemName}?</DialogTitle>
        <DialogDescription>
          This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button variant="destructive" onClick={onConfirm} data-testid="confirm-delete-btn">Delete</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default function SiteContentPage() {
  const [examples, setExamples] = useState([]);
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal states
  const [exampleModal, setExampleModal] = useState({ open: false, item: null });
  const [packageModal, setPackageModal] = useState({ open: false, item: null });
  const [statModal, setStatModal] = useState({ open: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, type: '', item: null });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [examplesRes, packagesRes, statsRes] = await Promise.all([
        siteContentApi.getExamples(),
        siteContentApi.getPackages(),
        siteContentApi.getStats()
      ]);
      setExamples(examplesRes.data.examples || []);
      setPackages(packagesRes.data.packages || []);
      setStats(statsRes.data.stats || []);
    } catch (error) {
      toast.error('Failed to load site content');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Example handlers
  const handleSaveExample = async (data) => {
    if (exampleModal.item) {
      await siteContentApi.updateExample(exampleModal.item.id, data);
      toast.success('Example updated');
    } else {
      await siteContentApi.createExample(data);
      toast.success('Example created');
    }
    fetchData();
  };

  const handleToggleExampleActive = async (example) => {
    await siteContentApi.updateExample(example.id, { active: !example.active });
    toast.success(example.active ? 'Example hidden' : 'Example visible');
    fetchData();
  };

  const handleDeleteExample = async () => {
    await siteContentApi.deleteExample(deleteModal.item.id);
    toast.success('Example deleted');
    setDeleteModal({ open: false, type: '', item: null });
    fetchData();
  };

  // Package handlers
  const handleSavePackage = async (data) => {
    if (packageModal.item) {
      await siteContentApi.updatePackage(packageModal.item.id, data);
      toast.success('Package updated');
    } else {
      await siteContentApi.createPackage(data);
      toast.success('Package created');
    }
    fetchData();
  };

  const handleTogglePackageActive = async (pkg) => {
    await siteContentApi.updatePackage(pkg.id, { active: !pkg.active });
    toast.success(pkg.active ? 'Package hidden' : 'Package visible');
    fetchData();
  };

  const handleTogglePackagePopular = async (pkg) => {
    await siteContentApi.updatePackage(pkg.id, { is_popular: !pkg.is_popular });
    toast.success(pkg.is_popular ? 'Removed popular badge' : 'Marked as popular');
    fetchData();
  };

  const handleDeletePackage = async () => {
    await siteContentApi.deletePackage(deleteModal.item.id);
    toast.success('Package deleted');
    setDeleteModal({ open: false, type: '', item: null });
    fetchData();
  };

  // Stat handlers
  const handleSaveStat = async (data) => {
    if (statModal.item) {
      await siteContentApi.updateStat(statModal.item.id, data);
      toast.success('Stat updated');
    } else {
      await siteContentApi.createStat(data);
      toast.success('Stat created');
    }
    fetchData();
  };

  const handleToggleStatActive = async (stat) => {
    await siteContentApi.updateStat(stat.id, { active: !stat.active });
    toast.success(stat.active ? 'Stat hidden' : 'Stat visible');
    fetchData();
  };

  const handleDeleteStat = async () => {
    await siteContentApi.deleteStat(deleteModal.item.id);
    toast.success('Stat deleted');
    setDeleteModal({ open: false, type: '', item: null });
    fetchData();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Site Content</h1>
        <p className="text-slate-500">Manage your marketing website content</p>
      </div>

      <Tabs defaultValue="examples" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="examples" className="flex items-center gap-2" data-testid="tab-examples">
            <Image className="h-4 w-4" />
            Examples
          </TabsTrigger>
          <TabsTrigger value="packages" className="flex items-center gap-2" data-testid="tab-packages">
            <Package className="h-4 w-4" />
            Packages
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center gap-2" data-testid="tab-stats">
            <BarChart3 className="h-4 w-4" />
            Stats
          </TabsTrigger>
        </TabsList>

        {/* Examples Tab */}
        <TabsContent value="examples" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">{examples.length} example sites</p>
            <Button onClick={() => setExampleModal({ open: true, item: null })} data-testid="add-example-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Example
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {examples.map((example) => (
              <ExampleCard 
                key={example.id}
                example={example}
                onEdit={(ex) => setExampleModal({ open: true, item: ex })}
                onDelete={(ex) => setDeleteModal({ open: true, type: 'example', item: ex })}
                onToggleActive={handleToggleExampleActive}
              />
            ))}
          </div>
          {examples.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No example sites yet. Add your first one!
            </div>
          )}
        </TabsContent>

        {/* Packages Tab */}
        <TabsContent value="packages" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">{packages.length} pricing packages</p>
            <Button onClick={() => setPackageModal({ open: true, item: null })} data-testid="add-package-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Package
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <PackageCard 
                key={pkg.id}
                pkg={pkg}
                onEdit={(p) => setPackageModal({ open: true, item: p })}
                onDelete={(p) => setDeleteModal({ open: true, type: 'package', item: p })}
                onToggleActive={handleTogglePackageActive}
                onTogglePopular={handleTogglePackagePopular}
              />
            ))}
          </div>
          {packages.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No packages yet. Add your first one!
            </div>
          )}
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">{stats.length} stats</p>
            <Button onClick={() => setStatModal({ open: true, item: null })} data-testid="add-stat-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Stat
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <StatCard 
                key={stat.id}
                stat={stat}
                onEdit={(s) => setStatModal({ open: true, item: s })}
                onDelete={(s) => setDeleteModal({ open: true, type: 'stat', item: s })}
                onToggleActive={handleToggleStatActive}
              />
            ))}
          </div>
          {stats.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No stats yet. Add your first one!
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <ExampleFormModal 
        open={exampleModal.open}
        onClose={() => setExampleModal({ open: false, item: null })}
        example={exampleModal.item}
        onSave={handleSaveExample}
      />
      <PackageFormModal 
        open={packageModal.open}
        onClose={() => setPackageModal({ open: false, item: null })}
        pkg={packageModal.item}
        onSave={handleSavePackage}
      />
      <StatFormModal 
        open={statModal.open}
        onClose={() => setStatModal({ open: false, item: null })}
        stat={statModal.item}
        onSave={handleSaveStat}
      />
      <DeleteConfirmModal 
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, type: '', item: null })}
        onConfirm={
          deleteModal.type === 'example' ? handleDeleteExample :
          deleteModal.type === 'package' ? handleDeletePackage :
          handleDeleteStat
        }
        itemName={deleteModal.item?.name || deleteModal.item?.label || 'item'}
      />
    </div>
  );
}
